import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { LinkedinLogo, TwitterLogo, Download, Copy, Check, Share, Link } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../lib/api";

/** Phase 245 — Share-your-badge dialog. Pulls the suggested social copy and
 * deep-links from the backend, lets the supplier copy/edit text, download
 * the 1080x1080 PNG, and one-tap-post to LinkedIn / X / native Web Share.
 */
export default function ShareBadgeDialog({ badge, open, onOpenChange }) {
  const [share, setShare] = useState(null);
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (!open || !badge?.id) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await API.get(`/suppliers/badges/${badge.id}/share-text`);
        if (cancel) return;
        setShare(data);
        setText(data.text || "");
      } catch {
        toast.error("Failed to load share details");
      }
    })();
    // Fetch image as blob for preview + download
    (async () => {
      try {
        const res = await API.get(`/suppliers/badges/${badge.id}/image`, { responseType: "blob" });
        if (cancel) return;
        const url = URL.createObjectURL(res.data);
        setImageUrl(url);
      } catch {}
    })();
    return () => {
      cancel = true;
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, badge?.id]);

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleCopyLink = async () => {
    if (!share?.landing_url) return;
    try {
      await navigator.clipboard.writeText(share.landing_url);
      toast.success("Public badge link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleDownload = () => {
    if (!imageUrl) return;
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `best-supplier-${(badge.vendor_name || "badge").replace(/\s+/g, "-").toLowerCase()}-${(badge.period_label || "").replace(/\s+/g, "-")}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Badge downloaded");
  };

  const handleNativeShare = async () => {
    try {
      if (!imageUrl) {
        // Fall back to text + URL
        await navigator.share({ text, url: share?.landing_url || "" });
        return;
      }
      const blob = await fetch(imageUrl).then(r => r.blob());
      const file = new File([blob], "best-supplier-badge.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, url: share?.landing_url || "" });
      } else {
        await navigator.share({ text, url: share?.landing_url || "" });
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Share failed");
    }
  };

  if (!badge) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="share-badge-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Share your {badge.tier_label} Supplier badge</DialogTitle>
        </DialogHeader>

        {imageUrl && (
          <div className="rounded-lg overflow-hidden border border-border bg-muted/30 flex items-center justify-center">
            <img
              src={imageUrl}
              alt={`${badge.tier_label} Best Supplier badge for ${badge.vendor_name}`}
              className="max-h-[260px] w-auto"
              data-testid="share-badge-preview-img"
            />
          </div>
        )}

        <div className="space-y-2">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Suggested post text</label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            className="text-xs resize-none"
            data-testid="share-badge-text"
          />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCopyText} className="flex-1" data-testid="share-copy-text-btn">
              {copied ? <Check size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
              {copied ? "Copied" : "Copy text"}
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopyLink} className="flex-1" disabled={!share?.landing_url} data-testid="share-copy-link-btn">
              <Link size={14} className="mr-1.5" />
              Copy link
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button onClick={handleDownload} disabled={!imageUrl} data-testid="share-download-btn">
            <Download size={14} className="mr-1.5" />
            Download PNG
          </Button>
          {typeof navigator !== "undefined" && navigator.share && (
            <Button onClick={handleNativeShare} variant="outline" data-testid="share-native-btn">
              <Share size={14} className="mr-1.5" />
              Share…
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <a
            href={share?.linkedin_share_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium text-white shadow-sm transition-opacity ${share?.linkedin_share_url ? "hover:opacity-90" : "opacity-50 pointer-events-none"}`}
            style={{ background: "#0A66C2" }}
            data-testid="share-linkedin-btn"
          >
            <LinkedinLogo size={14} weight="fill" />
            Post to LinkedIn
          </a>
          <a
            href={share?.twitter_share_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium text-white shadow-sm transition-opacity ${share?.twitter_share_url ? "hover:opacity-90" : "opacity-50 pointer-events-none"}`}
            style={{ background: "#0F1419" }}
            data-testid="share-twitter-btn"
          >
            <TwitterLogo size={14} weight="fill" />
            Post to X
          </a>
        </div>

        <p className="text-[10px] text-muted-foreground text-center pt-1">
          Your badge link is public — anyone you share it with can view (but not modify) the certificate.
        </p>
      </DialogContent>
    </Dialog>
  );
}
